import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const PublicNavbar = ({ transparent = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll for navbar transparency
  useEffect(() => {
    if (transparent) {
      const handleScroll = () => {
        setScrolled(window.scrollY > 50);
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [transparent]);

  // Check if we're on a page where anchor links make sense
  const contentPages = ["/for-athletes", "/for-adults", "/home"];
  const isContentPage = contentPages.includes(location.pathname);

  // Remember the last content page for logo navigation
  useEffect(() => {
    if (contentPages.includes(location.pathname)) {
      sessionStorage.setItem("paladin_last_page", location.pathname);
    }
  }, [location.pathname]);

  const logoTarget = isContentPage ? location.pathname : (sessionStorage.getItem("paladin_last_page") || "/");

  // Handle navigation to home page sections
  const handleSectionClick = (section) => {
    setMenuOpen(false);
    if (isContentPage) {
      document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(`${logoTarget}#${section}`);
    }
  };

  const navBackground = transparent && !scrolled 
    ? "bg-transparent" 
    : "bg-[#070B14]/95 backdrop-blur-xl border-b border-white/5";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`}>
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="home-logo">
            <img src="/paladin-logo.jpeg" alt="Paladin" className="w-10 h-10 rounded-lg object-contain" />
            <div className="hidden sm:block">
              <span className="font-bold text-lg tracking-wider text-white">PALADIN</span>
              <span className="block text-[9px] text-gray-400 tracking-[0.2em] uppercase">Health & Human Performance</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm tracking-[0.15em] uppercase transition-colors ${
                location.pathname === "/" ? "text-blue-400" : "text-white/80 hover:text-white"
              }`}
            >
              Home
            </Link>
            <button 
              onClick={() => handleSectionClick('about')}
              className="text-sm tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              About
            </button>
            <button 
              onClick={() => handleSectionClick('programs')}
              className="text-sm tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              Programs
            </button>
            <button 
              onClick={() => handleSectionClick('results')}
              className="text-sm tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors bg-transparent border-none cursor-pointer"
            >
              Results
            </button>
            <Link 
              to="/philosophy" 
              className={`text-sm tracking-[0.15em] uppercase transition-colors ${
                location.pathname === "/philosophy" ? "text-amber-400" : "text-white/80 hover:text-white"
              }`}
            >
              Philosophy
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link 
              to="/login"
              className={`text-sm tracking-[0.15em] uppercase transition-colors ${
                location.pathname === "/login" ? "text-blue-400" : "text-white/80 hover:text-white"
              }`}
              data-testid="nav-signin"
            >
              Sign In
            </Link>
            <Link 
              to="/pricing"
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white px-6 py-2.5 rounded-full text-sm tracking-[0.15em] uppercase font-medium transition-all shadow-lg shadow-blue-500/25"
              data-testid="nav-get-started"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden p-2 text-white relative z-50"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 top-0 left-0 right-0 bottom-0 z-40 bg-[#070B14]/98 backdrop-blur-xl"
            >
              <div className="flex flex-col justify-center items-start h-full px-8 pb-20 gap-5">
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className={`py-2 text-2xl tracking-[0.15em] uppercase font-medium ${
                    location.pathname === "/" ? "text-blue-400" : "text-white/90 hover:text-white"
                  }`}
                >
                  Home
                </Link>
                <button 
                  onClick={() => handleSectionClick('about')}
                  className="text-white/90 hover:text-white py-2 text-left text-2xl tracking-[0.15em] uppercase font-medium bg-transparent border-none cursor-pointer"
                >
                  About
                </button>
                <button 
                  onClick={() => handleSectionClick('programs')}
                  className="text-white/90 hover:text-white py-2 text-left text-2xl tracking-[0.15em] uppercase font-medium bg-transparent border-none cursor-pointer"
                >
                  Programs
                </button>
                <button 
                  onClick={() => handleSectionClick('results')}
                  className="text-white/90 hover:text-white py-2 text-left text-2xl tracking-[0.15em] uppercase font-medium bg-transparent border-none cursor-pointer"
                >
                  Results
                </button>
                <Link 
                  to="/philosophy" 
                  onClick={() => setMenuOpen(false)} 
                  className={`py-2 text-2xl tracking-[0.15em] uppercase font-medium ${
                    location.pathname === "/philosophy" ? "text-amber-400" : "text-white/90 hover:text-white"
                  }`}
                >
                  Philosophy
                </Link>
                <Link 
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className={`py-2 text-2xl tracking-[0.15em] uppercase font-medium ${
                    location.pathname === "/login" ? "text-blue-400" : "text-white/90 hover:text-white"
                  }`}
                >
                  Sign In
                </Link>
                <Link 
                  to="/pricing"
                  onClick={() => setMenuOpen(false)}
                  className="mt-6 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-8 py-3.5 rounded-full text-center text-lg tracking-[0.15em] uppercase font-semibold w-full"
                >
                  Start Training
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default PublicNavbar;
